import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

export const useApi = (apiFunction, dependencies = [], options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestInProgress, setRequestInProgress] = useState(false);

  const { 
    immediate = true, 
    onSuccess, 
    onError,
    showToast = true 
  } = options;

  const execute = async (...args) => {
    // Prevent duplicate simultaneous requests
    if (requestInProgress) {
      console.log('Request already in progress, skipping...');
      return { success: false, error: 'Request already in progress' };
    }

    try {
      setRequestInProgress(true);
      setLoading(true);
      setError(null);

      const response = await apiFunction(...args);
      const result = response.data;

      // For admin dashboard stats, preserve the full structure  
      setData(result);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      if (showToast && result.message) {
        toast.success(result.message);
      }

      return { success: true, data: result.data || result };
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      
      if (onError) {
        onError(err);
      }
      
      if (showToast) {
        toast.error(errorMessage);
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
      setRequestInProgress(false);
    }
  };

  const refetch = useCallback(() => execute(), []);

  useEffect(() => {
    if (immediate && apiFunction) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    data,
    loading,
    error,
    execute,
    refetch,
  };
};

export const useMutation = (apiFunction, options = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { 
    onSuccess, 
    onError,
    showToast = true 
  } = options;

  const mutate = async (...args) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFunction(...args);
      const result = response.data;

      if (onSuccess) {
        onSuccess(result);
      }
      
      if (showToast && result.message) {
        toast.success(result.message);
      }

      return { success: true, data: result.data || result };
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      
      if (onError) {
        onError(err);
      }
      
      if (showToast) {
        toast.error(errorMessage);
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return {
    mutate,
    loading,
    error,
  };
};